import { ulid } from 'ulid';
import { type ZodError, z } from 'zod';

// ============================================
// BRANDED TYPES
// ============================================

export const PlayerId = z.ulid().brand('PlayerId');
export type PlayerId = z.infer<typeof PlayerId>;

export const TeamId = z.ulid().brand('TeamId');
export type TeamId = z.infer<typeof TeamId>;

export const PlayerGroupId = z.ulid().brand('PlayerGroupId');
export type PlayerGroupId = z.infer<typeof PlayerGroupId>;

export const SessionId = z.ulid().brand('SessionId');
export type SessionId = z.infer<typeof SessionId>;

export const RoundId = z.ulid().brand('RoundId');
export type RoundId = z.infer<typeof RoundId>;

export const GameDefinitionId = z.ulid().brand('GameDefinitionId');
export type GameDefinitionId = z.infer<typeof GameDefinitionId>;

export const EventId = z.ulid().brand('EventId');
export type EventId = z.infer<typeof EventId>;

export const UserId = z.uuid().brand('UserId');
export type UserId = z.infer<typeof UserId>;

export const SessionTemplateId = z.ulid().brand('SessionTemplateId');
export type SessionTemplateId = z.infer<typeof SessionTemplateId>;

export const ImageId = z.ulid().brand('ImageId');
export type ImageId = z.infer<typeof ImageId>;

export const InviteTokenId = z.ulid().brand('InviteTokenId');
export type InviteTokenId = z.infer<typeof InviteTokenId>;

export const RequestId = z.ulid().brand('RequestId');
export type RequestId = z.infer<typeof RequestId>;

export const SpectatorId = z.ulid().brand('SpectatorId');
export type SpectatorId = z.infer<typeof SpectatorId>;

// ============================================
// BRANDED TYPES HELPERS
// ============================================

// PlayerId helpers
export function createPlayerId(value?: string): PlayerId {
  return PlayerId.parse(value ?? ulid());
}

export function parsePlayerId(value: string): PlayerId {
  return PlayerId.parse(value);
}

export function safeParsePlayerId(
  value: string,
): { success: true; data: PlayerId } | { success: false; error: ZodError } {
  const result = PlayerId.safeParse(value);
  if (result.success) {
    return { success: true, data: result.data };
  }
  return { success: false, error: result.error };
}

// TeamId helpers
export function createTeamId(value?: string): TeamId {
  return TeamId.parse(value ?? ulid());
}

export function parseTeamId(value: string): TeamId {
  return TeamId.parse(value);
}

export function safeParseTeamId(
  value: string,
): { success: true; data: TeamId } | { success: false; error: ZodError } {
  const result = TeamId.safeParse(value);
  if (result.success) {
    return { success: true, data: result.data };
  }
  return { success: false, error: result.error };
}

// PlayerGroupId helpers
export function createPlayerGroupId(value?: string): PlayerGroupId {
  return PlayerGroupId.parse(value ?? ulid());
}

export function parsePlayerGroupId(value: string): PlayerGroupId {
  return PlayerGroupId.parse(value);
}

export function safeParsePlayerGroupId(
  value: string,
): { success: true; data: PlayerGroupId } | { success: false; error: ZodError } {
  const result = PlayerGroupId.safeParse(value);
  if (result.success) {
    return { success: true, data: result.data };
  }
  return { success: false, error: result.error };
}

// SessionId helpers
export function createSessionId(value?: string): SessionId {
  return SessionId.parse(value ?? ulid());
}

export function parseSessionId(value: string): SessionId {
  return SessionId.parse(value);
}

export function safeParseSessionId(
  value: string,
): { success: true; data: SessionId } | { success: false; error: ZodError } {
  const result = SessionId.safeParse(value);
  if (result.success) {
    return { success: true, data: result.data };
  }
  return { success: false, error: result.error };
}

// RoundId helpers
export function createRoundId(value?: string): RoundId {
  return RoundId.parse(value ?? ulid());
}

export function parseRoundId(value: string): RoundId {
  return RoundId.parse(value);
}

export function safeParseRoundId(
  value: string,
): { success: true; data: RoundId } | { success: false; error: ZodError } {
  const result = RoundId.safeParse(value);
  if (result.success) {
    return { success: true, data: result.data };
  }
  return { success: false, error: result.error };
}

// GameDefinitionId helpers
export function createGameDefinitionId(value?: string): GameDefinitionId {
  return GameDefinitionId.parse(value ?? ulid());
}

export function parseGameDefinitionId(value: string): GameDefinitionId {
  return GameDefinitionId.parse(value);
}

export function safeParseGameDefinitionId(
  value: string,
): { success: true; data: GameDefinitionId } | { success: false; error: ZodError } {
  const result = GameDefinitionId.safeParse(value);
  if (result.success) {
    return { success: true, data: result.data };
  }
  return { success: false, error: result.error };
}

// EventId helpers
export function createEventId(value?: string): EventId {
  return EventId.parse(value ?? ulid());
}

export function parseEventId(value: string): EventId {
  return EventId.parse(value);
}

export function safeParseEventId(
  value: string,
): { success: true; data: EventId } | { success: false; error: ZodError } {
  const result = EventId.safeParse(value);
  if (result.success) {
    return { success: true, data: result.data };
  }
  return { success: false, error: result.error };
}

// UserId helpers
export function createUserId(value?: string): UserId {
  return UserId.parse(value ?? ulid());
}

export function parseUserId(value: string): UserId {
  return UserId.parse(value);
}

export function safeParseUserId(
  value: string,
): { success: true; data: UserId } | { success: false; error: ZodError } {
  const result = UserId.safeParse(value);
  if (result.success) {
    return { success: true, data: result.data };
  }
  return { success: false, error: result.error };
}

// SessionTemplateId helpers
export function createSessionTemplateId(value?: string): SessionTemplateId {
  return SessionTemplateId.parse(value ?? ulid());
}

export function parseSessionTemplateId(value: string): SessionTemplateId {
  return SessionTemplateId.parse(value);
}

export function safeParseSessionTemplateId(
  value: string,
): { success: true; data: SessionTemplateId } | { success: false; error: ZodError } {
  const result = SessionTemplateId.safeParse(value);
  if (result.success) {
    return { success: true, data: result.data };
  }
  return { success: false, error: result.error };
}

// ImageId helpers
export function createImageId(value?: string): ImageId {
  return ImageId.parse(value ?? ulid());
}

export function parseImageId(value: string): ImageId {
  return ImageId.parse(value);
}

export function safeParseImageId(
  value: string,
): { success: true; data: ImageId } | { success: false; error: ZodError } {
  const result = ImageId.safeParse(value);
  if (result.success) {
    return { success: true, data: result.data };
  }
  return { success: false, error: result.error };
}

// InviteTokenId helpers
export function createInviteTokenId(value?: string): InviteTokenId {
  return InviteTokenId.parse(value ?? ulid());
}

export function parseInviteTokenId(value: string): InviteTokenId {
  return InviteTokenId.parse(value);
}

export function safeParseInviteTokenId(
  value: string,
): { success: true; data: InviteTokenId } | { success: false; error: ZodError } {
  const result = InviteTokenId.safeParse(value);
  if (result.success) {
    return { success: true, data: result.data };
  }
  return { success: false, error: result.error };
}

// RequestId helpers
export function createRequestId(value?: string): RequestId {
  return RequestId.parse(value ?? ulid());
}

export function parseRequestId(value: string): RequestId {
  return RequestId.parse(value);
}

export function safeParseRequestId(
  value: string,
): { success: true; data: RequestId } | { success: false; error: ZodError } {
  const result = RequestId.safeParse(value);
  if (result.success) {
    return { success: true, data: result.data };
  }
  return { success: false, error: result.error };
}

// SpectatorId helpers
export function createSpectatorId(value?: string): SpectatorId {
  return SpectatorId.parse(value ?? ulid());
}

export function parseSpectatorId(value: string): SpectatorId {
  return SpectatorId.parse(value);
}

export function safeParseSpectatorId(
  value: string,
): { success: true; data: SpectatorId } | { success: false; error: ZodError } {
  const result = SpectatorId.safeParse(value);
  if (result.success) {
    return { success: true, data: result.data };
  }
  return { success: false, error: result.error };
}
